/**
 * ChatPanel.tsx — LLM chat panel that can modify simulation state directly.
 *
 * Calls llm.core-loop.dev (Qwen2.5 14B via Ollama).
 * Streams responses, extracts JSON update blocks, and fires onParamsChange
 * so the simulation updates in real time.
 */

import { useState, useRef, useEffect, useCallback } from 'react'
import type { SimulationParams } from '../engine/types'
import { chatComplete, type ChatMessage } from '../llm/client'
import { SYSTEM_PROMPT, buildContext } from '../llm/promptBuilder'
import { extractUpdate, stripJsonBlock, applyUpdate } from '../llm/paramExtractor'

interface Props {
  params: SimulationParams
  onParamsChange: (updated: SimulationParams) => void
  activeScenario: string | null
  onScenarioChange: (name: string | null) => void
  onRunSimulation: () => void
}

interface Message {
  id: string
  role: 'user' | 'assistant'
  content: string        // display text (JSON block stripped)
  rawContent?: string    // full text including JSON
  hasUpdate?: boolean
}

const QUICK_SCENARIOS = [
  { label: 'Job loss', prompt: 'Simulate losing my job completely.' },
  { label: 'Medical emergency', prompt: 'Simulate a serious medical emergency that costs $150k out of pocket.' },
  { label: 'Market crash (35%)', prompt: 'Simulate a major market crash — 35% portfolio drop and slower recovery.' },
  { label: 'Early retirement', prompt: 'What if I retired 10 years early?' },
  { label: 'Second income', prompt: 'Simulate adding a second household income of $80k/yr.' },
  { label: 'No insurance', prompt: 'Simulate a medical emergency with no health insurance.' },
  { label: 'Buy a home', prompt: 'Simulate buying a $500k home with 20% down.' },
  { label: 'Divorce', prompt: 'Simulate a divorce — split assets in half, lose partner income.' },
  { label: '2008-style crash', prompt: 'Simulate a 2008-style crash: 50% equity drop, 3yr recovery.' },
]

export function ChatPanel({ params, onParamsChange, activeScenario, onScenarioChange, onRunSimulation }: Props) {
  const [messages, setMessages] = useState<Message[]>([])
  const [input, setInput] = useState('')
  const [streaming, setStreaming] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const abortRef = useRef<AbortController | null>(null)
  const bottomRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  const buildHistory = useCallback((): ChatMessage[] => {
    const context = buildContext(params)
    const system: ChatMessage = {
      role: 'system',
      content: `${SYSTEM_PROMPT}\n\n${context}`,
    }
    const history: ChatMessage[] = messages
      .filter(m => m.role === 'user' || m.role === 'assistant')
      .map(m => ({ role: m.role, content: m.rawContent ?? m.content }))
    return [system, ...history]
  }, [params, messages])

  const sendMessage = useCallback(async (text: string) => {
    if (!text.trim() || streaming) return

    const userMsg: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: text.trim(),
    }

    const assistantId = `${Date.now()}-a`
    const assistantMsg: Message = {
      id: assistantId,
      role: 'assistant',
      content: '',
    }

    setMessages(prev => [...prev, userMsg, assistantMsg])
    setInput('')
    setStreaming(true)
    setError(null)

    const history = buildHistory()
    history.push({ role: 'user', content: text.trim() })

    const abort = new AbortController()
    abortRef.current = abort

    let full = ''

    try {
      await chatComplete(
        history,
        delta => {
          full += delta
          setMessages(prev =>
            prev.map(m =>
              m.id === assistantId
                ? { ...m, content: stripJsonBlock(full), rawContent: full }
                : m,
            ),
          )
        },
        abort.signal,
      )

      // Try to extract and apply a param update
      const update = extractUpdate(full)
      if (update?.update) {
        const updated = applyUpdate(params, update)
        onParamsChange(updated)
        if (update.scenario) onScenarioChange(update.scenario)
        // Flag the message as having an update
        setMessages(prev =>
          prev.map(m =>
            m.id === assistantId ? { ...m, hasUpdate: true } : m,
          ),
        )
        // Auto-run simulation after a param change
        setTimeout(onRunSimulation, 100)
      }
    } catch (err: unknown) {
      if ((err as Error).name === 'AbortError') return
      const msg = err instanceof Error ? err.message : String(err)
      const isEndpointDown = msg.includes('Failed to fetch') || msg.includes('NetworkError') || msg.includes('fetch')
      const displayMsg = isEndpointDown
        ? 'LLM service unreachable. Check VITE_LLM_PROXY_TARGET in .env.local, or try again later. Manual simulation and events still work.'
        : msg
      setError(displayMsg)
      setMessages(prev => prev.filter(m => m.id !== assistantId))
    } finally {
      setStreaming(false)
      abortRef.current = null
    }
  }, [streaming, buildHistory, params, onParamsChange, onScenarioChange, onRunSimulation])

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      sendMessage(input)
    }
  }

  function handleReset() {
    setMessages([])
    setError(null)
    onScenarioChange(null)
  }

  function handleAbort() {
    abortRef.current?.abort()
  }

  return (
    <div className="chat-panel">
      {/* Header */}
      <div className="chat-header">
        <span className="chat-title">AI Scenario Builder</span>
        {activeScenario && (
          <span className="scenario-badge">
            <span className="scenario-dot" />
            {activeScenario}
          </span>
        )}
        <button className="chat-reset-btn" onClick={handleReset} title="Clear chat and reset scenario">
          Clear
        </button>
      </div>

      {/* Quick scenario chips */}
      {messages.length === 0 && (
        <div className="chat-quickstart">
          <p className="chat-quickstart-label">Try a scenario:</p>
          <div className="chat-chips">
            {QUICK_SCENARIOS.filter(s => s.prompt).map(s => (
              <button
                key={s.label}
                className="chat-chip"
                onClick={() => sendMessage(s.prompt!)}
                disabled={streaming}
              >
                {s.label}
              </button>
            ))}
          </div>
          <p className="chat-quickstart-hint">
            Or describe any financial situation — the AI will adjust your simulation.
          </p>
        </div>
      )}

      {/* Message thread */}
      <div className="chat-messages">
        {messages.map(m => (
          <div key={m.id} className={`chat-message chat-message--${m.role}`}>
            <div className="chat-bubble">
              {m.content || <span className="chat-cursor" />}
              {m.hasUpdate && (
                <div className="chat-applied-badge">✓ Simulation updated</div>
              )}
            </div>
          </div>
        ))}
        {error && (
          <div className="chat-error">
            ⚠ {error}
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div className="chat-input-row">
        <textarea
          ref={textareaRef}
          className="chat-input"
          placeholder="Describe a scenario or ask a question…"
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          rows={2}
          disabled={streaming}
        />
        {streaming ? (
          <button className="chat-send-btn chat-send-btn--stop" onClick={handleAbort}>
            Stop
          </button>
        ) : (
          <button
            className="chat-send-btn"
            onClick={() => sendMessage(input)}
            disabled={!input.trim()}
          >
            Send
          </button>
        )}
      </div>
    </div>
  )
}
